import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { useSupportNotifications } from '../../context/SupportNotificationsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { ImageLightbox } from '../../components/ui/image-lightbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  ArrowLeft, Building2, User, Clock, Send, Loader2,
  AlertTriangle, MessageSquare, Lock, Crown, Star, Paperclip, X, FileText, Image, File, Clipboard, Users,
  ArrowUpCircle, ArrowDownCircle, CalendarIcon, Timer, Tag, Plus, Trash2, ChevronDown, MessageSquarePlus, Pencil, Check, Download, Copy, ExternalLink, RefreshCw
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';

const ROLE_HIERARCHY = { support: 0, manager: 1, admin: 2 };

const TicketDetail = () => {
  const { isDark = true } = useOutletContext() || {};
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { api, admin } = useSuperAdmin();
  const { markRead: markSupportTicketRead } = useSupportNotifications();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Assignment modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [adminsList, setAdminsList] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  // JIRA work item linking
  const [jiraInput, setJiraInput] = useState('');
  const [jiraStatus, setJiraStatus] = useState(null);
  const [jiraStatusLoading, setJiraStatusLoading] = useState(false);
  const [jiraWriteConfigured, setJiraWriteConfigured] = useState(false);
  const [creatingJiraIssue, setCreatingJiraIssue] = useState(false);
  const [pushingCommentId, setPushingCommentId] = useState(null);
  const [postCommentToJira, setPostCommentToJira] = useState(false);

  // Escalation state
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalateDirection, setEscalateDirection] = useState('up');
  const [escalateTargets, setEscalateTargets] = useState([]);
  const [selectedEscalateTarget, setSelectedEscalateTarget] = useState('');
  const [escalateReason, setEscalateReason] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [loadingTargets, setLoadingTargets] = useState(false);

  // Time tracking state
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [timeHours, setTimeHours] = useState('');
  const [timeMinutes, setTimeMinutes] = useState('');
  const [timeNote, setTimeNote] = useState('');
  const [loggingTime, setLoggingTime] = useState(false);
  const [showTimeLog, setShowTimeLog] = useState(false);

  // Tags state
  const [availableTags, setAvailableTags] = useState([]);
  const [showTagPopover, setShowTagPopover] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#ffd108');
  const [creatingTag, setCreatingTag] = useState(false);

  // Impersonate-from-ticket state
  const [impersonating, setImpersonating] = useState(false);

  const isManagerOrAdmin = admin?.role === 'manager' || admin?.role === 'admin';

  // One-click impersonate the ticket's tenant (opens the tenant's system in a new tab).
  // Resolves the tenant's best admin/dispatcher user, then reuses the existing
  // impersonation endpoint. No need to navigate to the Tenants page first.
  const handleImpersonateTenant = async () => {
    if (!ticket?.client_id) {
      toast.error('This ticket has no linked tenant');
      return;
    }
    setImpersonating(true);
    try {
      const res = await api.get(`/api/super-admin/tenants/${ticket.client_id}`);
      const users = res.data?.stats?.users || [];
      if (!users.length) {
        toast.error('No tenant users available to impersonate');
        return;
      }
      const active = users.filter(u => u.is_active !== false);
      const pool = active.length ? active : users;
      // Prefer an admin, then a dispatcher, then any user.
      const pick =
        pool.find(u => u.role === 'admin') ||
        pool.find(u => u.role === 'dispatcher') ||
        pool[0];
      const imp = await api.post(`/api/super-admin/tenants/${ticket.client_id}/impersonate?user_id=${pick.id}`);
      const redirectPath = imp.data.redirect_path || '/dashboard';
      const url = `${window.location.origin}${redirectPath}?impersonate=${imp.data.access_token}`;
      window.open(url, '_blank', 'noopener');
      toast.success(`Impersonating ${pick.full_name || pick.username} — opened in a new tab`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to impersonate tenant');
    } finally {
      setImpersonating(false);
    }
  };

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
        setTicket(res.data);
        setJiraInput(res.data?.jira_work_item || '');
        if (res.data?.jira_work_item) fetchJiraStatus();
        // Mark as read so the super admin sidebar badge clears
        markSupportTicketRead(ticketId);
      } catch (err) {
        console.error('Failed to fetch ticket:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
    // Check whether JIRA "create issue" is configured (site+creds+project+type)
    api.get('/api/super-admin/settings/jira')
      .then((res) => setJiraWriteConfigured(!!res.data?.write_configured))
      .catch(() => setJiraWriteConfigured(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, ticketId]);

  const fetchComments = async () => {
    try {
      const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
      setTicket(res.data);
    } catch (err) {
      console.error('Failed to refresh comments:', err);
    }
  };

  const createJiraIssue = async () => {
    setCreatingJiraIssue(true);
    try {
      const res = await api.post(`/api/super-admin/tickets/${ticketId}/jira-issue`);
      const key = res.data?.issue_key;
      setTicket(prev => ({ ...prev, jira_work_item: key }));
      setJiraInput(key || '');
      if (res.data?.status) setJiraStatus(res.data.status); else fetchJiraStatus();
      toast.success(`Created JIRA issue ${key}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = d?.jira?.detail || d?.message || (typeof d === 'string' ? d : 'Failed to create JIRA issue');
      toast.error(msg);
    } finally {
      setCreatingJiraIssue(false);
    }
  };

  const pushCommentToJira = async (commentId) => {
    setPushingCommentId(commentId);
    try {
      await api.post(`/api/super-admin/tickets/${ticketId}/comments/${commentId}/jira-push`);
      toast.success('Comment pushed to JIRA');
      fetchComments();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = d?.jira?.detail || d?.message || (typeof d === 'string' ? d : 'Failed to push comment');
      toast.error(msg);
    } finally {
      setPushingCommentId(null);
    }
  };

  const updateTicket = async (field, value) => {
    setSaving(true);
    try {
      await api.put(`/api/super-admin/tickets/${ticketId}`, { [field]: value });
      setTicket(prev => ({ ...prev, [field]: value }));
      if (field === 'jira_work_item') {
        if (value) fetchJiraStatus();
        else setJiraStatus(null);
      }
    } catch (err) {
      console.error('Failed to update ticket:', err);
    } finally {
      setSaving(false);
    }
  };

  const fetchJiraStatus = async () => {
    setJiraStatusLoading(true);
    try {
      const res = await api.get(`/api/super-admin/tickets/${ticketId}/jira-status`);
      setJiraStatus(res.data);
    } catch (err) {
      setJiraStatus({ ok: false, error: 'api_error' });
    } finally {
      setJiraStatusLoading(false);
    }
  };

  // Fetch admins for assignment modal
  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const res = await api.get('/api/super-admin/admins');
      setAdminsList(res.data);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    } finally {
      setLoadingAdmins(false);
    }
  };

  // Open assignment modal
  const openAssignModal = () => {
    fetchAdmins();
    setSelectedAdminId(ticket?.assigned_to_id || '');
    setShowAssignModal(true);
  };

  // Assign ticket to selected admin
  const handleAssignTicket = async () => {
    if (!selectedAdminId) return;
    
    const selectedAdmin = adminsList.find(a => a.id === selectedAdminId);
    if (!selectedAdmin) return;
    
    setSaving(true);
    try {
      await api.put(`/api/super-admin/tickets/${ticketId}`, { 
        assigned_to_id: selectedAdmin.id,
        assigned_to_name: selectedAdmin.full_name
      });
      setTicket(prev => ({ 
        ...prev, 
        assigned_to_id: selectedAdmin.id,
        assigned_to_name: selectedAdmin.full_name
      }));
      setShowAssignModal(false);
    } catch (err) {
      console.error('Failed to assign ticket:', err);
    } finally {
      setSaving(false);
    }
  };

  // Unassign ticket
  const handleUnassignTicket = async () => {
    setSaving(true);
    try {
      await api.put(`/api/super-admin/tickets/${ticketId}`, { 
        assigned_to_id: null,
        assigned_to_name: null
      });
      setTicket(prev => ({ 
        ...prev, 
        assigned_to_id: null,
        assigned_to_name: null
      }));
      setShowAssignModal(false);
    } catch (err) {
      console.error('Failed to unassign ticket:', err);
    } finally {
      setSaving(false);
    }
  };

  // Escalation handlers
  const openEscalateModal = async (direction) => {
    setEscalateDirection(direction);
    setSelectedEscalateTarget('');
    setEscalateReason('');
    setShowEscalateModal(true);
    setLoadingTargets(true);
    try {
      const res = await api.get(`/api/super-admin/tickets/${ticketId}/escalation-targets?direction=${direction}`);
      setEscalateTargets(res.data);
    } catch (err) {
      console.error('Failed to load escalation targets:', err);
      setEscalateTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  };

  const handleEscalate = async () => {
    if (!selectedEscalateTarget || !escalateReason.trim()) return;
    setEscalating(true);
    try {
      const endpoint = escalateDirection === 'up' ? 'escalate' : 'de-escalate';
      await api.post(`/api/super-admin/tickets/${ticketId}/${endpoint}`, {
        to_id: selectedEscalateTarget,
        reason: escalateReason.trim()
      });
      // Refresh ticket
      const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
      setTicket(res.data);
      setShowEscalateModal(false);
    } catch (err) {
      console.error(`Failed to ${escalateDirection === 'up' ? 'escalate' : 'de-escalate'}:`, err);
    } finally {
      setEscalating(false);
    }
  };

  const canEscalate = admin && ROLE_HIERARCHY[admin.role] < 2;
  const canDeescalate = admin && ROLE_HIERARCHY[admin.role] >= 1 && ticket?.is_escalated;

  // Fetch available tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await api.get('/api/super-admin/ticket-tags');
        setAvailableTags(res.data);
      } catch (err) { console.error('Failed to fetch tags:', err); }
    };
    fetchTags();
  }, [api]);

  // Time tracking handlers
  const handleLogTime = async () => {
    const totalMinutes = (parseInt(timeHours) || 0) * 60 + (parseInt(timeMinutes) || 0);
    if (totalMinutes <= 0) return;
    setLoggingTime(true);
    try {
      await api.post(`/api/super-admin/tickets/${ticketId}/time-entries`, {
        minutes: totalMinutes,
        note: timeNote.trim() || null
      });
      const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
      setTicket(res.data);
      setShowTimeModal(false);
      setTimeHours('');
      setTimeMinutes('');
      setTimeNote('');
    } catch (err) {
      console.error('Failed to log time:', err);
    } finally {
      setLoggingTime(false);
    }
  };

  const getTotalTime = () => {
    const entries = ticket?.time_entries || [];
    const total = entries.reduce((sum, e) => sum + (e.minutes || 0), 0);
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0 && m === 0) return 'No time logged';
    return `${h > 0 ? `${h}h ` : ''}${m > 0 ? `${m}m` : ''}`.trim();
  };

  const getMyTime = () => {
    const entries = (ticket?.time_entries || []).filter(e => e.logged_by_id === admin?.id);
    return entries.reduce((sum, e) => sum + (e.minutes || 0), 0);
  };

  // Tag handlers
  const toggleTag = async (tagId) => {
    if (!isManagerOrAdmin) return;
    const currentTags = ticket?.tags || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter(t => t !== tagId)
      : [...currentTags, tagId];
    try {
      await api.put(`/api/super-admin/tickets/${ticketId}`, { tags: newTags });
      setTicket(prev => ({ ...prev, tags: newTags }));
    } catch (err) {
      console.error('Failed to update tags:', err);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      const res = await api.post('/api/super-admin/ticket-tags', {
        name: newTagName.trim(),
        color: newTagColor
      });
      setAvailableTags(prev => [...prev, res.data.tag]);
      setNewTagName('');
    } catch (err) {
      console.error('Failed to create tag:', err);
    } finally {
      setCreatingTag(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/api/super-admin/ticket-tags/${tagId}`);
      setAvailableTags(prev => prev.filter(t => t.id !== tagId));
      setTicket(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tagId) }));
    } catch (err) {
      console.error('Failed to delete tag:', err);
    }
  };

  // Handle paste event for images
  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        if (file.size > 5 * 1024 * 1024) {
          alert('Pasted image is too large. Maximum size is 5MB.');
          continue;
        }

        setUploading(true);
        try {
          const reader = new FileReader();
          const dataUrl = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
          setAttachments(prev => [...prev, {
            filename: `pasted-screenshot-${timestamp}.png`,
            url: dataUrl,
            content_type: file.type,
            size: file.size,
            isPasted: true
          }]);
        } catch (err) {
          console.error('Failed to read pasted image:', err);
        } finally {
          setUploading(false);
        }
      }
    }
  }, []);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const newAttachments = [];
    
    for (const file of files) {
      // For now, we'll convert to base64 data URL (for small files)
      // In production, you'd upload to S3/cloud storage
      if (file.size > 5 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }
      
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      
      newAttachments.push({
        filename: file.name,
        url: dataUrl,
        content_type: file.type,
        size: file.size
      });
    }
    
    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf') || contentType?.includes('document')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const addComment = async () => {
    if (!newComment.trim() && attachments.length === 0) return;
    setSendingComment(true);
    try {
      await api.post(`/api/super-admin/tickets/${ticketId}/comments`, {
        content: newComment,
        is_internal: isInternal,
        post_to_jira: postCommentToJira && !!ticket.jira_work_item,
        attachments: attachments.length > 0 ? attachments : null
      });
      // Refresh ticket
      const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
      setTicket(res.data);
      setNewComment('');
      setIsInternal(false);
      setPostCommentToJira(false);
      setAttachments([]);
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSendingComment(false);
    }
  };

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState(null);

  const startEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
  };
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditContent('');
  };
  const saveEditComment = async (commentId) => {
    if (!editContent.trim()) return;
    setSavingEdit(true);
    try {
      await api.put(`/api/super-admin/tickets/${ticketId}/comments/${commentId}`, { content: editContent });
      const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
      setTicket(res.data);
      cancelEditComment();
    } catch (err) {
      console.error('Failed to edit comment:', err);
    } finally {
      setSavingEdit(false);
    }
  };
  const deleteComment = async () => {
    if (!deleteCommentId) return;
    try {
      await api.delete(`/api/super-admin/tickets/${ticketId}/comments/${deleteCommentId}`);
      const res = await api.get(`/api/super-admin/tickets/${ticketId}`);
      setTicket(res.data);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setDeleteCommentId(null);
    }
  };

  // ── Export ticket transcript (frontend-only; built from the loaded ticket) ──
  const buildTranscript = (fmt = 'markdown') => {
    const md = fmt === 'markdown';
    const fmtDate = (d) => { try { return format(new Date(d), 'PPpp'); } catch { return d || ''; } };
    const lines = [];
    const comments = ticket.comments || [];
    if (md) {
      lines.push(`# Support Ticket ${ticket.ticket_number || ''} — ${ticket.subject || ''}`);
      lines.push('');
      lines.push(`- **Status:** ${ticket.status?.replace(/_/g, ' ') || '-'}`);
      lines.push(`- **Priority:** ${ticket.priority || '-'}`);
      lines.push(`- **Category:** ${ticket.category?.replace(/_/g, ' ') || '-'}`);
      lines.push(`- **Tenant:** ${ticket.company_name || '-'} (${ticket.tenant_tier || '-'})`);
      lines.push(`- **Submitted by:** ${ticket.submitted_by_name || '-'}`);
      lines.push(`- **Created:** ${fmtDate(ticket.created_at)}`);
      lines.push('');
      lines.push('## Description');
      lines.push('');
      lines.push(ticket.description || '(no description)');
      lines.push('');
      lines.push('## Conversation');
      lines.push('');
      if (!comments.length) lines.push('_No comments._');
      comments.forEach((c, i) => {
        const role = c.author_type === 'support' ? 'Support' : 'Customer';
        const tags = [];
        if (c.is_internal) tags.push('internal note');
        if (c.edited_at) tags.push('edited');
        const tagStr = tags.length ? ` _(${tags.join(', ')})_` : '';
        lines.push(`### ${i + 1}. ${c.author_name || 'Unknown'} — ${role}${tagStr}`);
        lines.push(`*${fmtDate(c.created_at)}*`);
        lines.push('');
        lines.push(c.content || '');
        if (c.attachments?.length) {
          lines.push('');
          lines.push(`Attachments: ${c.attachments.map(a => a.filename).join(', ')}`);
        }
        lines.push('');
      });
    } else {
      lines.push(`Support Ticket ${ticket.ticket_number || ''} — ${ticket.subject || ''}`);
      lines.push(`Status: ${ticket.status?.replace(/_/g, ' ') || '-'}`);
      lines.push(`Priority: ${ticket.priority || '-'}`);
      lines.push(`Category: ${ticket.category?.replace(/_/g, ' ') || '-'}`);
      lines.push(`Tenant: ${ticket.company_name || '-'} (${ticket.tenant_tier || '-'})`);
      lines.push(`Submitted by: ${ticket.submitted_by_name || '-'}`);
      lines.push(`Created: ${fmtDate(ticket.created_at)}`);
      lines.push('');
      lines.push('DESCRIPTION');
      lines.push(ticket.description || '(no description)');
      lines.push('');
      lines.push('CONVERSATION');
      lines.push('');
      if (!comments.length) lines.push('No comments.');
      comments.forEach((c, i) => {
        const role = c.author_type === 'support' ? 'Support' : 'Customer';
        const tags = [];
        if (c.is_internal) tags.push('internal note');
        if (c.edited_at) tags.push('edited');
        const tagStr = tags.length ? ` (${tags.join(', ')})` : '';
        lines.push(`[${i + 1}] ${c.author_name || 'Unknown'} — ${role}${tagStr} — ${fmtDate(c.created_at)}`);
        lines.push(c.content || '');
        if (c.attachments?.length) lines.push(`Attachments: ${c.attachments.map(a => a.filename).join(', ')}`);
        lines.push('');
      });
    }
    return lines.join('\n');
  };

  const copyTranscript = async (fmt) => {
    try {
      await navigator.clipboard.writeText(buildTranscript(fmt));
      toast.success(`Transcript copied (${fmt === 'markdown' ? 'Markdown' : 'plain text'})`);
    } catch (err) {
      toast.error('Failed to copy transcript');
    }
  };

  const downloadTranscript = (fmt) => {
    const content = buildTranscript(fmt);
    const ext = fmt === 'markdown' ? 'md' : 'txt';
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ticket.ticket_number || 'ticket'}-transcript.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Transcript downloaded (.${ext})`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-purple-500/20 text-purple-400 border-purple-500';
      case 'open': return 'bg-blue-500/20 text-blue-400 border-blue-500';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
      case 'waiting_on_customer': return 'bg-orange-500/20 text-orange-400 border-orange-500';
      case 'waiting_on_support': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500';
      case 'resolved': return 'bg-green-500/20 text-green-400 border-green-500';
      case 'closed': return 'bg-slate-500/20 text-slate-400 border-slate-500';
      case 'reopened_by_customer': return 'bg-red-500/20 text-red-400 border-red-500';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-400';
      case 'high': return 'text-orange-400';
      case 'medium': return 'text-yellow-400';
      default: return 'text-slate-400';
    }
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'professional':
        return (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            <Crown className="h-3 w-3 mr-1" />
            Professional
          </Badge>
        );
      case 'advanced':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Star className="h-3 w-3 mr-1" />
            Advanced
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
            Basic
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Ticket not found</p>
        <Button variant="ghost" onClick={() => navigate('/super-admin/tickets')} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tickets
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/super-admin/tickets')}>
          <ArrowLeft className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-[#ffd108] font-mono">{ticket.ticket_number}</span>
            <Badge className={getStatusColor(ticket.status)}>
              {ticket.status?.replace(/_/g, ' ')}
            </Badge>
            {getTierBadge(ticket.tenant_tier)}
            {ticket.is_escalated && (
              <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 animate-pulse">
                <ArrowUpCircle className="h-3 w-3 mr-1" />
                Escalated
              </Badge>
            )}
            {ticket.source === 'ea_feedback' && (
              <Badge data-testid="ticket-detail-ea-badge" className="bg-[#ffd108]/15 text-[#ffd108] border-[#ffd108]/30">
                <MessageSquarePlus className="h-3 w-3 mr-1" />
                EA Feedback
              </Badge>
            )}
          </div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.subject}</h1>
          <div className={`flex items-center gap-4 mt-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            <span className="flex items-center gap-1">
              <Building2 className="h-4 w-4" /> {ticket.company_name}
            </span>
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" /> {ticket.submitted_by_name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" /> {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
            </span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="export-transcript-btn" className={isDark ? 'border-slate-600 text-slate-200' : ''}>
              <Download className="h-4 w-4 mr-2" /> Export transcript
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Copy to clipboard</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => copyTranscript('markdown')} data-testid="copy-transcript-md">
              <Copy className="h-4 w-4 mr-2" /> Copy as Markdown
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => copyTranscript('plain')} data-testid="copy-transcript-txt">
              <Copy className="h-4 w-4 mr-2" /> Copy as plain text
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Download file</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => downloadTranscript('markdown')} data-testid="download-transcript-md">
              <FileText className="h-4 w-4 mr-2" /> Download .md
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => downloadTranscript('plain')} data-testid="download-transcript-txt">
              <FileText className="h-4 w-4 mr-2" /> Download .txt
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{ticket.description}</p>

              {/* Ticket-level Attachments (uploaded during creation) */}
              {ticket.attachments && ticket.attachments.length > 0 && (
                <div className={`mt-4 pt-4 border-t space-y-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    <Paperclip className="h-3.5 w-3.5 inline mr-1" />Attachments
                  </p>
                  {ticket.attachments.map((file, idx) => {
                    const FileIcon = getFileIcon(file.content_type);
                    const isImage = file.content_type?.startsWith('image/');
                    return (
                      <div key={`ticket-attach-${idx}`} data-testid={`ticket-attachment-${idx}`}>
                        {isImage ? (
                          <button type="button" onClick={() => setLightboxUrl(file.url)} className="block cursor-zoom-in" data-testid={`ticket-img-btn-${idx}`}>
                            <img
                              src={file.url}
                              alt={file.filename}
                              className="max-w-sm rounded-lg border hover:opacity-90 transition-opacity"
                            />
                          </button>
                        ) : (
                          <a
                            href={file.url}
                            download={file.filename}
                            className={`inline-flex items-center gap-2 p-2 rounded-lg transition-colors ${isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'}`}
                          >
                            <FileIcon className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                            <span className={`text-sm ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{file.filename}</span>
                            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>({formatFileSize(file.size)})</span>
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`text-lg flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <MessageSquare className="h-5 w-5" /> Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticket.comments?.length === 0 ? (
                <p className={`text-center py-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>No comments yet</p>
              ) : (
                <div className="space-y-4">
                  {ticket.comments?.map((comment) => (
                    <div
                      key={comment.id}
                      data-testid={`comment-${comment.id}`}
                      className={`p-4 rounded-lg ${
                        comment.is_internal
                          ? 'bg-yellow-500/10 border border-yellow-500/30'
                          : comment.author_type === 'support'
                          ? 'bg-[#ffd108]/10 border border-[#ffd108]/30'
                          : isDark ? 'bg-slate-700/50' : 'bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{comment.author_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {comment.author_type === 'support' ? 'Support' : 'Customer'}
                          </Badge>
                          {comment.is_internal && (
                            <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                              <Lock className="h-3 w-3 mr-1" /> Internal
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                            {format(new Date(comment.created_at), 'PPp')}
                            {comment.edited_at && <span className="italic ml-1">(edited)</span>}
                          </span>
                          {comment.author_type === 'support' && comment.author_id === admin?.id && editingCommentId !== comment.id && (
                            <>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => startEditComment(comment)} data-testid={`admin-edit-comment-${comment.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:text-red-500" onClick={() => setDeleteCommentId(comment.id)} data-testid={`admin-delete-comment-${comment.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                          {ticket.jira_work_item && (
                            comment.jira_synced ? (
                              <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-green-400' : 'text-green-600'}`} title="Synced to JIRA" data-testid={`comment-jira-synced-${comment.id}`}>
                                <Check className="h-3.5 w-3.5" /> JIRA
                              </span>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => pushCommentToJira(comment.id)}
                                disabled={pushingCommentId === comment.id}
                                title="Push this comment to the linked JIRA issue"
                                data-testid={`comment-push-jira-${comment.id}`}
                              >
                                {pushingCommentId === comment.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ExternalLink className="h-3.5 w-3.5 mr-1" /> Push to JIRA</>}
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                      {editingCommentId === comment.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={3}
                            data-testid={`admin-edit-comment-textarea-${comment.id}`}
                          />
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={() => saveEditComment(comment.id)} disabled={savingEdit || !editContent.trim()} data-testid={`admin-save-comment-${comment.id}`}>
                              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelEditComment} data-testid={`admin-cancel-edit-comment-${comment.id}`}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <p className={`whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{comment.content}</p>
                      )}
                      
                      {/* Comment Attachments */}
                      {comment.attachments && comment.attachments.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {comment.attachments.map((file, idx) => {
                            const FileIcon = getFileIcon(file.content_type);
                            const isImage = file.content_type?.startsWith('image/');
                            return (
                              <div key={`entry-${idx}`}>
                                {isImage ? (
                                  <button
                                    type="button"
                                    onClick={() => setLightboxUrl(file.url)}
                                    className="block cursor-zoom-in"
                                    data-testid={`comment-img-btn-${idx}`}
                                  >
                                    <img 
                                      src={file.url} 
                                      alt={file.filename}
                                      className="max-w-sm rounded-lg border border-slate-600 hover:opacity-90 transition-opacity"
                                    />
                                  </button>
                                ) : (
                                  <a
                                    href={file.url}
                                    download={file.filename}
                                    className={`inline-flex items-center gap-2 p-2 rounded-lg ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
                                  >
                                    <FileIcon className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                                    <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{file.filename}</span>
                                    <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>({formatFileSize(file.size)})</span>
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add Comment */}
              <div className={`pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="relative">
                  <Textarea
                    ref={textareaRef}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onPaste={handlePaste}
                    placeholder="Add a comment... (Tip: You can paste screenshots directly!)"
                    className={`mb-3 pr-10 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                    rows={3}
                  />
                  <div className="absolute right-3 top-3">
                    <Clipboard className={`h-4 w-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} title="Paste images with Ctrl+V" />
                  </div>
                </div>
                
                {/* Attachments Preview - show pasted images inline */}
                {attachments.length > 0 && (
                  <div className="mb-3 space-y-2">
                    {attachments.map((file, index) => {
                      const FileIcon = getFileIcon(file.content_type);
                      const isImage = file.content_type?.startsWith('image/');
                      
                      // Show pasted images inline
                      if (isImage && file.isPasted) {
                        return (
                          <div key={`attach-${index}`} className="relative inline-block">
                            <img 
                              src={file.url} 
                              alt={file.filename}
                              className={`max-w-xs rounded-lg border ${isDark ? 'border-slate-600' : 'border-gray-300'}`}
                            />
                            <button
                              onClick={() => removeAttachment(index)}
                              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <span className="absolute bottom-2 left-2 text-xs bg-black/50 text-white px-2 py-1 rounded">
                              Pasted screenshot
                            </span>
                          </div>
                        );
                      }
                      
                      return (
                        <div 
                          key={`te-${index}`}
                          className={`flex items-center gap-3 p-2 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}
                        >
                          <FileIcon className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{file.filename}</p>
                            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{formatFileSize(file.size)}</p>
                          </div>
                          <button
                            onClick={() => removeAttachment(index)}
                            className={`p-1 rounded hover:bg-red-500/20 ${isDark ? 'text-slate-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500'}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="internal"
                        checked={isInternal}
                        onCheckedChange={setIsInternal}
                      />
                      <Label htmlFor="internal" className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        Internal note
                      </Label>
                    </div>

                    {ticket.jira_work_item && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="post-jira"
                          checked={postCommentToJira}
                          onCheckedChange={setPostCommentToJira}
                          data-testid="comment-post-to-jira-checkbox"
                        />
                        <Label htmlFor="post-jira" className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          Also post to JIRA
                        </Label>
                      </div>
                    )}
                    
                    {/* File Attachment Button */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className={isDark ? 'border-slate-600 text-slate-400 hover:text-white' : 'border-gray-200 text-gray-500 hover:text-gray-900'}
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><Paperclip className="h-4 w-4 mr-1" /> Attach</>
                      )}
                    </Button>
                  </div>
                  
                  <Button
                    onClick={addComment}
                    disabled={(!newComment.trim() && attachments.length === 0) || sendingComment}
                    className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                  >
                    {sendingComment ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <><Send className="h-4 w-4 mr-2" /> Send</>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</Label>
                <Select
                  value={ticket.status}
                  onValueChange={(v) => updateTicket('status', v)}
                  disabled={saving}
                >
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="waiting_on_customer">Waiting on Customer</SelectItem>
                    <SelectItem value="waiting_on_support">Waiting on Support</SelectItem>
                    <SelectItem value="reopened_by_customer">Reopened by Customer</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Priority</Label>
                <Select
                  value={ticket.priority}
                  onValueChange={(v) => updateTicket('priority', v)}
                  disabled={saving}
                >
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">
                      <span className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-400" /> Urgent
                      </span>
                    </SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category (now editable) */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Category</Label>
                <Select
                  value={ticket.category}
                  onValueChange={(v) => updateTicket('category', v)}
                  disabled={saving}
                >
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="feature_request">Feature Request</SelectItem>
                    <SelectItem value="bug_report">Bug Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Due Date */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Due Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${
                        isDark ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-gray-50 border-gray-200 text-gray-900'
                      } ${ticket.due_date && new Date(ticket.due_date) < new Date() ? 'text-red-400 border-red-500/50' : ''}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {ticket.due_date
                        ? format(new Date(ticket.due_date), 'PP')
                        : 'Set due date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={ticket.due_date ? new Date(ticket.due_date) : undefined}
                      onSelect={(date) => {
                        updateTicket('due_date', date ? date.toISOString() : null);
                      }}
                    />
                    {ticket.due_date && (
                      <div className="p-2 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-red-400"
                          onClick={() => updateTicket('due_date', null)}
                        >
                          <X className="h-3 w-3 mr-1" /> Clear due date
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              {/* JIRA Work Item */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>JIRA Work Item</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={jiraInput}
                    onChange={(e) => setJiraInput(e.target.value)}
                    onBlur={() => {
                      const v = jiraInput.trim();
                      if (v !== (ticket.jira_work_item || '')) {
                        updateTicket('jira_work_item', v || null);
                      }
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    placeholder="e.g. SUP-123 or a JIRA link"
                    disabled={saving}
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                    data-testid="jira-work-item-input"
                  />
                  {ticket.jira_work_item && /^https?:\/\//i.test(ticket.jira_work_item) && (
                    <a
                      href={ticket.jira_work_item}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex-shrink-0 ${isDark ? 'text-slate-400 hover:text-[#ffd108]' : 'text-gray-500 hover:text-gray-900'}`}
                      title="Open JIRA work item"
                      data-testid="jira-work-item-link"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
                {!ticket.jira_work_item && jiraWriteConfigured && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={createJiraIssue}
                    disabled={creatingJiraIssue}
                    className="w-full mt-1"
                    data-testid="create-jira-issue-button"
                  >
                    {creatingJiraIssue ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating in JIRA…</>
                    ) : (
                      <><Plus className="h-4 w-4 mr-2" /> Create JIRA issue from this ticket</>
                    )}
                  </Button>
                )}
                {!ticket.jira_work_item && !jiraWriteConfigured && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} data-testid="jira-not-configured-hint">
                    JIRA create-issue not fully configured — set Project Key &amp; Issue Type in{' '}
                    <span className="underline cursor-pointer" onClick={() => navigate('/super-admin/system-settings')}>System Settings → Integrations</span>
                  </p>
                )}
                {ticket.jira_work_item && (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="jira-status-badge">
                    {jiraStatusLoading ? (
                      <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        <Loader2 className="h-3 w-3 animate-spin" /> Checking JIRA…
                      </span>
                    ) : jiraStatus?.ok ? (
                      <>
                        {jiraStatus.url && (
                          <a
                            href={jiraStatus.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#ffd108] hover:underline flex items-center gap-1"
                            title="Open in JIRA"
                            data-testid="jira-browse-link"
                          >
                            {jiraStatus.issue_key || ticket.jira_work_item}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {jiraStatus.issue_type && (
                          <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`} title={`Type: ${jiraStatus.issue_type}`}>
                            {jiraStatus.issue_type_icon && (
                              <img src={jiraStatus.issue_type_icon} alt="" className="h-4 w-4" />
                            )}
                            {jiraStatus.issue_type}
                          </span>
                        )}
                        <Badge className={
                          jiraStatus.category === 'done'
                            ? 'bg-green-500/20 text-green-500 border border-green-500/30'
                            : jiraStatus.category === 'in_progress'
                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                        }>
                          {jiraStatus.status_name}
                        </Badge>
                        <button
                          onClick={fetchJiraStatus}
                          className={`text-xs ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                          title="Refresh JIRA status"
                          data-testid="jira-status-refresh"
                        >
                          <RefreshCw className="h-3 w-3" />
                        </button>
                      </>
                    ) : jiraStatus && jiraStatus.error !== 'invalid_key' ? (
                      <span className={`text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                        {{
                          not_configured: 'JIRA not configured (System Settings → Integrations)',
                          not_found: 'JIRA issue not found',
                          auth_error: 'JIRA auth failed — check credentials',
                          permission_denied: 'No access to this JIRA issue',
                        }[jiraStatus.error] || 'Could not reach JIRA'}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Assigned To</Label>
                <button
                  onClick={openAssignModal}
                  className={`w-full flex items-center gap-2 p-2 rounded-md text-left transition-colors ${
                    isDark
                      ? 'hover:bg-slate-700 text-white'
                      : 'hover:bg-gray-100 text-gray-900'
                  }`}
                  data-testid="assign-to-field"
                >
                  <User className={`h-4 w-4 flex-shrink-0 ${ticket.assigned_to_id === admin?.id ? 'text-[#ffd108]' : isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                  <span className={`flex-1 ${ticket.assigned_to_id === admin?.id ? 'text-[#ffd108] font-medium' : ''}`}>
                    {ticket.assigned_to_name
                      ? (ticket.assigned_to_id === admin?.id ? 'Me' : ticket.assigned_to_name)
                      : 'Unassigned'}
                  </span>
                  <ChevronDown className={`h-3 w-3 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                </button>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Tags</Label>
                  {isManagerOrAdmin && (
                    <Popover open={showTagPopover} onOpenChange={setShowTagPopover}>
                      <PopoverTrigger asChild>
                        <button className={`text-xs ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}>
                          Manage
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className={`w-72 ${isDark ? 'bg-slate-800 border-slate-700' : ''}`} align="end">
                        <div className="space-y-3">
                          <p className={`text-sm font-medium ${isDark ? 'text-white' : ''}`}>Manage Tags</p>
                          <div className="flex gap-2">
                            <Input
                              value={newTagName}
                              onChange={(e) => setNewTagName(e.target.value)}
                              placeholder="New tag name"
                              className={`text-sm h-8 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                            />
                            <input
                              type="color"
                              value={newTagColor}
                              onChange={(e) => setNewTagColor(e.target.value)}
                              className="h-8 w-8 rounded cursor-pointer border-0 p-0"
                            />
                            <Button
                              size="sm"
                              className="h-8 px-2 bg-[#ffd108] text-black hover:bg-[#e5bc07]"
                              onClick={handleCreateTag}
                              disabled={!newTagName.trim() || creatingTag}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {availableTags.map(tag => (
                              <div key={tag.id} className="flex items-center justify-between text-sm py-1">
                                <div className="flex items-center gap-2">
                                  <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                                  <span className={isDark ? 'text-slate-300' : 'text-gray-700'}>{tag.name}</span>
                                </div>
                                <button
                                  onClick={() => handleDeleteTag(tag.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            {availableTags.length === 0 && (
                              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>No tags created yet</p>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {availableTags.map(tag => {
                    const isActive = (ticket.tags || []).includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => isManagerOrAdmin && toggleTag(tag.id)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-all ${
                          isActive
                            ? 'border-transparent text-white font-medium'
                            : isManagerOrAdmin
                            ? `border-dashed ${isDark ? 'border-slate-600 text-slate-500 hover:border-slate-400 hover:text-slate-300' : 'border-gray-300 text-gray-400 hover:border-gray-400'}`
                            : 'hidden'
                        } ${!isManagerOrAdmin && isActive ? 'cursor-default' : isManagerOrAdmin ? 'cursor-pointer' : ''}`}
                        style={isActive ? { backgroundColor: tag.color } : {}}
                        disabled={!isManagerOrAdmin}
                      >
                        <Tag className="h-2.5 w-2.5" />
                        {tag.name}
                      </button>
                    );
                  })}
                  {(ticket.tags || []).length === 0 && availableTags.length === 0 && (
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>No tags</p>
                  )}
                </div>
              </div>

              {/* Time Tracking */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Time Tracked</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setShowTimeModal(true)}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Log
                  </Button>
                </div>
                <div className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <Timer className="h-4 w-4 text-[#ffd108]" />
                  <span className="text-sm font-medium">{getTotalTime()}</span>
                </div>
                {isManagerOrAdmin && (ticket?.time_entries || []).length > 0 && (
                  <div>
                    <button
                      onClick={() => setShowTimeLog(!showTimeLog)}
                      className={`text-xs flex items-center gap-1 ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      <ChevronDown className={`h-3 w-3 transition-transform ${showTimeLog ? 'rotate-180' : ''}`} />
                      {showTimeLog ? 'Hide' : 'Show'} time log ({(ticket?.time_entries || []).length})
                    </button>
                    {showTimeLog && (
                      <div className={`mt-2 space-y-1.5 text-xs max-h-40 overflow-y-auto ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {(ticket?.time_entries || []).map((entry) => {
                          const h = Math.floor(entry.minutes / 60);
                          const m = entry.minutes % 60;
                          return (
                            <div key={entry.id} className={`p-2 rounded ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                              <div className="flex justify-between">
                                <span className={isDark ? 'text-white' : 'text-gray-900'}>{entry.logged_by_name}</span>
                                <span className="font-medium text-[#ffd108]">{h > 0 ? `${h}h ` : ''}{m > 0 ? `${m}m` : ''}</span>
                              </div>
                              {entry.note && <p className="mt-0.5">{entry.note}</p>}
                              <p className="mt-0.5 opacity-60">{format(new Date(entry.timestamp), 'PP p')}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Created / Resolved dates */}
              <div className={`pt-4 border-t space-y-2 ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{format(new Date(ticket.created_at), 'PP')}</span>
                </div>
                {ticket.resolved_at && (
                  <div className="flex justify-between text-sm">
                    <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Resolved</span>
                    <span className="text-green-400">{format(new Date(ticket.resolved_at), 'PP')}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
            <CardHeader>
              <CardTitle className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => window.open(`/super-admin/tenants/${ticket.client_id}`, '_blank')}
              >
                <Building2 className="mr-2 h-4 w-4" /> View Tenant
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start border-[#ffd108]/40 text-[#ffd108] hover:bg-[#ffd108]/10 hover:text-[#ffd108]"
                onClick={handleImpersonateTenant}
                disabled={impersonating || !ticket.client_id}
                data-testid="impersonate-tenant-btn"
              >
                {impersonating
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <ExternalLink className="mr-2 h-4 w-4" />}
                Impersonate
              </Button>
              {/* Escalate button */}
              {canEscalate && (
                <Button
                  variant="outline"
                  className="w-full justify-start border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                  onClick={() => openEscalateModal('up')}
                  disabled={saving}
                >
                  <ArrowUpCircle className="mr-2 h-4 w-4" /> Escalate
                </Button>
              )}
              {/* De-escalate button */}
              {canDeescalate && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => openEscalateModal('down')}
                  disabled={saving}
                >
                  <ArrowDownCircle className="mr-2 h-4 w-4" /> De-escalate
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assignment Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Assign Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
              Select a team member to assign this ticket to:
            </p>
            {loadingAdmins ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
              </div>
            ) : (
              <Select value={selectedAdminId} onValueChange={setSelectedAdminId}>
                <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}>
                  <SelectValue placeholder="Select a team member" />
                </SelectTrigger>
                <SelectContent>
                  {adminsList.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{a.full_name}</span>
                        <Badge variant="outline" className="text-xs ml-2 capitalize">{a.role}</Badge>
                        {a.id === admin?.id && <span className="text-xs text-[#ffd108]">(Me)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* Quick-assign to me */}
            {ticket?.assigned_to_id !== admin?.id && (
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={async () => {
                  setSelectedAdminId(admin?.id);
                  setSaving(true);
                  try {
                    await api.put(`/api/super-admin/tickets/${ticketId}`, {
                      assigned_to_id: admin?.id,
                      assigned_to_name: admin?.full_name
                    });
                    setTicket(prev => ({
                      ...prev,
                      assigned_to_id: admin?.id,
                      assigned_to_name: admin?.full_name
                    }));
                    setShowAssignModal(false);
                  } catch (err) {
                    console.error('Failed to assign ticket:', err);
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                data-testid="assign-to-me-btn"
              >
                <User className="mr-2 h-4 w-4" /> Assign to Me
              </Button>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            {ticket?.assigned_to_id && (
              <Button
                variant="outline"
                onClick={handleUnassignTicket}
                disabled={saving}
                className="mr-auto"
              >
                Unassign
              </Button>
            )}
            <Button variant="ghost" onClick={() => setShowAssignModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignTicket} 
              disabled={!selectedAdminId || saving}
              className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Time Dialog */}
      <Dialog open={showTimeModal} onOpenChange={setShowTimeModal}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Log Time</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-4">
              <div className="space-y-2 flex-1">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Hours</Label>
                <Input
                  type="number"
                  min="0"
                  value={timeHours}
                  onChange={(e) => setTimeHours(e.target.value)}
                  placeholder="0"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}
                />
              </div>
              <div className="space-y-2 flex-1">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Minutes</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={timeMinutes}
                  onChange={(e) => setTimeMinutes(e.target.value)}
                  placeholder="0"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Note (optional)</Label>
              <Input
                value={timeNote}
                onChange={(e) => setTimeNote(e.target.value)}
                placeholder="What did you work on?"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTimeModal(false)}>Cancel</Button>
            <Button
              onClick={handleLogTime}
              disabled={((parseInt(timeHours) || 0) * 60 + (parseInt(timeMinutes) || 0)) <= 0 || loggingTime}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            >
              {loggingTime ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Log Time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalation Modal */}
      <Dialog open={showEscalateModal} onOpenChange={setShowEscalateModal}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              {escalateDirection === 'up' ? 'Escalate Ticket' : 'De-escalate Ticket'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {escalateDirection === 'up' && (
              <div className={`flex items-start gap-2 text-sm p-3 rounded-lg ${isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
                <ArrowUpCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>Priority will be bumped from <strong className="capitalize">{ticket?.priority}</strong> to <strong className="capitalize">
                  {ticket?.priority === 'low' ? 'medium' : ticket?.priority === 'medium' ? 'high' : 'urgent'}
                </strong>.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                {escalateDirection === 'up' ? 'Escalate to' : 'De-escalate to'}
              </Label>
              {loadingTargets ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-[#ffd108]" />
                </div>
              ) : escalateTargets.length === 0 ? (
                <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  No eligible agents found.
                </p>
              ) : (
                <Select value={selectedEscalateTarget} onValueChange={setSelectedEscalateTarget}>
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}>
                    <SelectValue placeholder="Select an agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {escalateTargets.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <span>{a.full_name}</span>
                          <Badge variant="outline" className="text-xs ml-2 capitalize">{a.role}</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Reason <span className="text-red-500">*</span></Label>
              <Textarea
                value={escalateReason}
                onChange={(e) => setEscalateReason(e.target.value)}
                placeholder={escalateDirection === 'up' 
                  ? 'Why does this ticket need escalation?' 
                  : 'Reason for de-escalation...'}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white min-h-[80px]' : 'bg-gray-50 border-gray-200 min-h-[80px]'}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEscalateModal(false)}>Cancel</Button>
            <Button
              onClick={handleEscalate}
              disabled={!selectedEscalateTarget || !escalateReason.trim() || escalating}
              className={escalateDirection === 'up' 
                ? 'bg-amber-500 text-black hover:bg-amber-400' 
                : 'bg-[#ffd108] text-black hover:bg-[#ffd108]/90'}
            >
              {escalating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                escalateDirection === 'up' ? 'Escalate' : 'De-escalate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete comment confirmation */}
      <Dialog open={!!deleteCommentId} onOpenChange={(open) => !open && setDeleteCommentId(null)}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''} data-testid="admin-delete-comment-dialog">
          <DialogHeader>
            <DialogTitle>Delete this comment?</DialogTitle>
          </DialogHeader>
          <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
            This will permanently remove your comment. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteCommentId(null)} data-testid="admin-delete-comment-cancel-btn">Cancel</Button>
            <Button variant="destructive" onClick={deleteComment} data-testid="admin-delete-comment-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImageLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </div>
  );
};

export default TicketDetail;
